import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import Field from "./Field";

function emptyFromFields(fields) {
  const obj = {};
  fields.forEach((f) => {
    if (f.type === "bool") obj[f.name] = false;
    else if (f.type === "number") obj[f.name] = 0;
    else if (f.type === "csv" || f.type === "list") obj[f.name] = [];
    else if (f.type === "select") obj[f.name] = f.options[0];
    else obj[f.name] = "";
  });
  return obj;
}

export default function CollectionEditor({ title, endpoint, fields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or "new"
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => {
    setForm(emptyFromFields(fields));
    setEditing("new");
  };
  const openEdit = (item) => {
    setForm({ ...emptyFromFields(fields), ...item });
    setEditing(item);
  };
  const close = () => { setEditing(null); setForm({}); };

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post(endpoint, form);
        toast.success(`${title} created.`);
      } else {
        await api.put(`${endpoint}/${editing.id}`, form);
        toast.success(`${title} updated.`);
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted.");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid={`admin-collection-${endpoint.slice(1)}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-2xl">{title}</h2>
        <button onClick={openNew} className="btn-volt" data-testid={`admin-new-${endpoint.slice(1)}`}>
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      {loading && <p className="text-fog-300">Loading…</p>}
      {!loading && items.length === 0 && <p className="text-fog-300">No items yet.</p>}

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.id} className="card-tech p-4 flex items-start justify-between gap-4" data-testid={`admin-row-${item.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-display font-semibold text-base truncate">
                {item.title || item.role || item.degree || item.category || item.name || item.src || item.filename || ""}
              </p>
              <p className="text-fog-300 text-sm truncate">
                {item.company || item.institution || item.caption || item.description || (item.items || []).slice(0, 4).join(", ") || item.quote || ""}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(item)} className="btn-ghost" data-testid={`admin-edit-${item.id}`}>
                <Pencil className="w-4 h-4" /> Edit
              </button>
              <button onClick={() => remove(item.id)} className="btn-ghost text-red-300 border-red-400/40 hover:text-red-200 hover:border-red-400" data-testid={`admin-delete-${item.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur flex items-center justify-center p-4" data-testid="admin-modal">
          <div className="card-tech max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{editing === "new" ? `New ${title}` : `Edit ${title}`}</h3>
              <button onClick={close} className="text-fog-300 hover:text-white" data-testid="admin-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              {fields.map((f) => (
                <Field key={f.name} field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
              ))}
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-3">
              <button onClick={close} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-volt" data-testid="admin-modal-save">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
